/**
 * What the create form asks for, per record type, and the rules that decide
 * whether a step may advance (CT-5525).
 *
 * Errors are CODES (`required` / `invalid`), never sentences: the screen turns
 * them into words with `t()`, so this file stays free of copy and trivially
 * testable. The server stays authoritative (422 / 409); this only spares the
 * user a round trip for what is obvious.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOMAIN_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const MIN_PHONE_DIGITS = 7;

let phoneSeq = 0;
/** Rows are keyed by id, not index: removing the first must not re-label the rest. */
export const newPhoneRow = (isPrimary = false) => ({ id: `phone-${++phoneSeq}`, number: '', isPrimary });

export const emptyValues = (entityType) => {
  if (entityType === 'customers') {
    return { fullName: '', email: '', phones: [newPhoneRow(true)], location: '', ownerId: '', ownerName: '' };
  }
  if (entityType === 'companies') {
    return { name: '', domain: '', industry: '', city: '', ownerId: '', ownerName: '' };
  }
  return {};
};

const blank = (value) => !String(value ?? '').trim();

/** Strips scheme, `www.`, path, query and hash, so a pasted URL is judged as the domain it names. */
const hostOf = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
    .replace(/^www\./, '')
    .split(/[/?#]/)[0];

const digitsIn = (value) => String(value ?? '').replace(/\D/g, '');

export const identityErrors = (entityType, values) => {
  const errors = {};
  if (entityType === 'customers') {
    if (blank(values.fullName)) errors.fullName = 'required';
    if (blank(values.email)) errors.email = 'required';
    else if (!EMAIL_PATTERN.test(values.email.trim())) errors.email = 'invalid';
  }
  if (entityType === 'companies') {
    if (blank(values.name)) errors.name = 'required';
    if (blank(values.domain)) errors.domain = 'required';
    else if (!DOMAIN_PATTERN.test(hostOf(values.domain))) errors.domain = 'invalid';
  }
  return errors;
};

export const detailsErrors = (entityType, values) => {
  const errors = {};
  if (entityType === 'customers') {
    if (blank(values.location)) errors.location = 'required';
    const badPhones = Object.fromEntries(
      (values.phones ?? [])
        .filter((phone) => !blank(phone.number) && digitsIn(phone.number).length < MIN_PHONE_DIGITS)
        .map((phone) => [phone.id, 'invalid']),
    );
    if (Object.keys(badPhones).length > 0) errors.phones = badPhones;
  }
  return errors;
};

export const isIdentityComplete = (entityType, values) =>
  Object.keys(identityErrors(entityType, values)).length === 0;

export const isDetailsComplete = (entityType, values) =>
  Object.keys(detailsErrors(entityType, values)).length === 0;

/** The snake_case body `createEntity` sends. */
export const buildCreatePayload = (entityType, values) => {
  if (entityType === 'customers') {
    const filled = (values.phones ?? []).filter((phone) => !blank(phone.number));
    // The primary flag belongs to a number that exists: if the row that carried
    // it was left empty, the first number that remains takes it over.
    const primaryId = (filled.find((phone) => phone.isPrimary) ?? filled[0])?.id;
    return {
      full_name: values.fullName.trim(),
      email: values.email.trim(),
      phones: filled.map((phone) => ({ number: phone.number.trim(), is_primary: phone.id === primaryId })),
      location: values.location,
      owner_id: values.ownerId || null,
    };
  }
  return {
    name: values.name.trim(),
    domain: values.domain.trim(),
    industry: values.industry || null,
    city: values.city || null,
    owner_id: values.ownerId || null,
  };
};
